'use client';

import React, { useState } from 'react';
import { Pencil, Trash2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { TeamMember } from './ClientTeamContent';
import Badge from '@/components/ui/Badge';
import Modal from '@/components/ui/Modal';

interface Props {
  members: TeamMember[];
}

const statusConfig = {
  active: 'text-positive bg-positive/10',
  invited: 'text-info bg-info/10',
  inactive: 'text-muted-foreground bg-muted',
};

export default function TeamMembersTable({ members }: Props) {
  const [deleteTarget, setDeleteTarget] = useState<TeamMember | null>(null);
  const [roleChangeTarget, setRoleChangeTarget] = useState<string | null>(null);

  const handleDelete = () => {
    toast.success(`${deleteTarget?.name} has been removed from the team`);
    setDeleteTarget(null);
  };

  return (
    <>
      <div className="card-base overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Member</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Role</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Assigned Clients</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Last Active</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</th>
                <th className="px-4 py-3 w-10"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {members.map((member) => (
                <tr key={member.id} className="table-row-hover group">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <span className="text-xs font-bold text-primary">{member.avatar}</span>
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{member.name}</p>
                        <p className="text-xs text-muted-foreground">{member.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="relative">
                      <button
                        onClick={() => setRoleChangeTarget(roleChangeTarget === member.id ? null : member.id)}
                        className="flex items-center gap-1 group/role"
                      >
                        <Badge variant={member.role}>{member.role.charAt(0).toUpperCase() + member.role.slice(1)}</Badge>
                        <ChevronDown size={11} className="text-muted-foreground opacity-0 group-hover/role:opacity-100 transition-opacity" />
                      </button>
                      {roleChangeTarget === member.id && (
                        <div className="absolute top-full mt-1 left-0 bg-card border border-border rounded-xl shadow-modal z-20 overflow-hidden w-36 fade-in">
                          {(['admin', 'manager', 'editor'] as const).map((role) => (
                            <button
                              key={`role-opt-${role}`}
                              onClick={() => { toast.success(`${member.name}'s role changed to ${role}`); setRoleChangeTarget(null); }}
                              className={`w-full px-3 py-2 text-left text-xs font-medium hover:bg-muted transition-colors flex items-center gap-2 ${member.role === role ? 'text-primary bg-primary/5' : 'text-foreground'}`}
                            >
                              <Badge variant={role}>{role.charAt(0).toUpperCase() + role.slice(1)}</Badge>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <span className="text-sm font-medium text-foreground tabular-nums">{member.assignedClients}</span>
                      {member.assignedClientNames.length > 0 && member.assignedClientNames[0] !== 'All clients' && (
                        <p className="text-xs text-muted-foreground mt-0.5 max-w-[180px] truncate">
                          {member.assignedClientNames.join(', ')}
                        </p>
                      )}
                      {member.assignedClientNames[0] === 'All clients' && (
                        <p className="text-xs text-muted-foreground mt-0.5">All clients</p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-muted-foreground">{member.lastActive}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig[member.status]}`}>
                      {member.status.charAt(0).toUpperCase() + member.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                      <button
                        onClick={() => toast.info(`Editing ${member.name}'s profile`)}
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-all"
                        title="Edit member"
                      >
                        <Pencil size={12} />
                      </button>
                      <button
                        onClick={() => setDeleteTarget(member)}
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-negative/10 hover:text-negative transition-all"
                        title="Remove member — this cannot be undone"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirm Modal */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Remove Team Member"
        subtitle="This action cannot be undone"
        size="sm"
      >
        <div className="p-6">
          <p className="text-sm text-muted-foreground leading-relaxed">
            Are you sure you want to remove{' '}
            <span className="font-semibold text-foreground">{deleteTarget?.name}</span> from the team?
            They will lose access to all client accounts immediately.
          </p>
          <div className="flex items-center justify-end gap-3 mt-6">
            <button onClick={() => setDeleteTarget(null)} className="btn-ghost">Cancel</button>
            <button onClick={handleDelete} className="btn-danger">Remove Member</button>
          </div>
        </div>
      </Modal>
    </>
  );
}